import { Check } from "lucide-react"
import type { BeginnerStep } from "@/entities/beginner-path"
import { useHaptic } from "@/shared/hooks/use-haptic"
import { pluralize } from "@/shared/lib/pluralize"
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/shared/ui/accordion"
import { ResourceRow } from "./resource-row"

const CARD = "overflow-hidden rounded-2xl bg-card ring-1 ring-white/10"

interface StepItemProps extends BeginnerStep {
	completed: boolean
	onToggle: () => void
	resolvePostId: (url: string) => string | undefined
}

function StepBullet({
	step,
	title,
	completed,
	onToggle,
}: {
	step: number
	title: string
	completed: boolean
	onToggle: () => void
}) {
	const { triggerHaptic } = useHaptic()

	return (
		<button
			type="button"
			aria-pressed={completed}
			aria-label={
				completed ? `Снять отметку с шага «${title}»` : `Отметить шаг «${title}» пройденным`
			}
			onClick={() => {
				triggerHaptic(completed ? "light" : "medium")
				onToggle()
			}}
			className={`absolute top-4 left-0 grid size-8 min-h-8! place-items-center rounded-full text-sm font-bold ring-4 ring-background transition-transform duration-150 ease-[var(--ease-out-soft)] after:absolute after:-inset-1.5 after:content-[''] active:scale-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
				completed
					? "bg-primary text-primary-foreground"
					: "bg-white/10 text-foreground inset-ring-1 inset-ring-white/15"
			}`}
		>
			{completed ? <Check className="size-4" strokeWidth={3} /> : step}
		</button>
	)
}

function StepHead({
	title,
	description,
	completed,
}: {
	title: string
	description?: string
	completed: boolean
}) {
	return (
		<span className="min-w-0 flex-1 text-left">
			<span className="block font-semibold leading-tight">{title}</span>
			{description ? (
				<span className="mt-1 block text-sm leading-snug text-muted-foreground">{description}</span>
			) : null}
			{completed ? (
				<span className="mt-2 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2.5 py-1 text-xs font-medium text-primary">
					Пройден
				</span>
			) : null}
		</span>
	)
}

export function StepItem({
	step,
	title,
	description,
	links,
	completed,
	onToggle,
	resolvePostId,
}: StepItemProps) {
	const bullet = <StepBullet step={step} title={title} completed={completed} onToggle={onToggle} />

	if (links.length === 0) {
		return (
			<div className="relative pl-12">
				{bullet}
				<div className={`${CARD} px-4 py-4`}>
					<StepHead title={title} description={description} completed={completed} />
					{completed ? null : (
						<span className="mt-3 inline-flex rounded-full bg-white/5 px-2.5 py-1 text-xs text-muted-foreground">
							Скоро добавим материалы
						</span>
					)}
				</div>
			</div>
		)
	}

	return (
		<AccordionItem value={`step-${step}`} className="relative border-b-0 pl-12">
			{bullet}
			<div className={CARD}>
				<AccordionTrigger className="items-start gap-3 px-4 py-4 hover:no-underline [&>svg]:mt-1">
					<StepHead title={title} description={description} completed={completed} />
				</AccordionTrigger>
				<AccordionContent className="px-2 pt-0 pb-2">
					<p className="px-2 pb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
						{pluralize(links.length, ["материал", "материала", "материалов"])}
					</p>
					{links.map((link) => (
						<ResourceRow
							key={`${link.url}-${link.label}`}
							label={link.label}
							url={link.url}
							resolvedPostId={resolvePostId(link.url)}
						/>
					))}
				</AccordionContent>
			</div>
		</AccordionItem>
	)
}
